import { Inject, Injectable, OnDestroy } from "@angular/core";
import { DwButtonConfig, DwEventService, DwMediaEntity, DwMediaModalService, DwMediaSelectOptions, DwMenuService, DwModalButtonStandardConfigs, DwModalResult, DwModalService, DwOrmDataService, DwOrmDataServiceToken, DwRoutingService } from "@devwareapps/devware-cap";
import { AcsWrittenResultEntity, AcsWrittenTestStatusAllItems, AppMetaData, AppMetaDataItemNames } from "../../../meta-data/app-meta-data.service";
import { CourseRepositoryService } from "../../courses/services/course-repository.service";
import { StudentRepositoryService } from "./student-repository.service";
import { catchError, distinctUntilChanged, filter, map, mergeMap, switchMap, take, takeUntil, timeout } from "rxjs/operators";
import { merge, Observable, of, Subject, throwError, timer } from "rxjs";
import { DwPubSubEventTopics } from "../../z-devware/pubsub/models/dw-pub-sub-event-topics.model";


@Injectable({
    providedIn: 'root'
})
export class ACSSubmissionUtilService implements OnDestroy {

    public static ACS_WRITTEN_RESULT_TOPIC = 'AcsWrittenResultTopic';

    private readonly ACS_INTERVAL_CHECK_TIME_MS = 5000;
    private readonly ACS_TIMEOUT_MS = 120000;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private dwModalService: DwModalService,
        private dwMediaModalService: DwMediaModalService,
        private dwMenuService: DwMenuService,
        private dwRoutingService: DwRoutingService,
        private courseRespositoryService: CourseRepositoryService,
        private dwEventService: DwEventService,
        @Inject(DwOrmDataServiceToken) private dataService: DwOrmDataService,
        private studentRepositoryService: StudentRepositoryService,
    ) {

    }

    showSelectFilesModal(files: FileList, fileControl: any = null): Observable<DwMediaEntity> {

        const mediaSelectOptions: DwMediaSelectOptions = {
            allowedFileTypes: '.pdf,.jpg,.jpeg,.png',
            uploadInstructions: 'Upload a PDF or image of your ACS Written Exam',
            isSecure: false,
            imageResizeOptions: undefined,
            parentItemName: AppMetaDataItemNames.AcsWrittenResult,
            parentAttributeName: AppMetaData.AcsWrittenResult.Attributes.TestResultMediaId.fieldName
        };

        return this.dwMediaModalService.showSelectFileModal(mediaSelectOptions, files);
    }

    processACSTestFile(result: DwMediaEntity, studentCourseId?: number, courseAcsTestId?: number): Observable<AcsWrittenResultEntity> {
        const acsWrittenResult = AppMetaData.AcsWrittenResult.CreateEntity();
        acsWrittenResult.TestResultMediaId = result.MediaId;

        acsWrittenResult.StudentCourseId = studentCourseId;
        acsWrittenResult.AcsTestId = courseAcsTestId;
        acsWrittenResult.AcsWrittenTestStatusId = AcsWrittenTestStatusAllItems.New;

        const buttonConfig = [DwModalButtonStandardConfigs.instance.okButton];
        buttonConfig[0].buttonClass = 'btn btn-primary';

        let ob: Observable<any>;

        return this.dataService.saveEntity(acsWrittenResult, true)
            .pipe(mergeMap((acsWrittenResult: AcsWrittenResultEntity) => {

                return this.waitForCompletion(acsWrittenResult)
                    .pipe(mergeMap((completedResult: AcsWrittenResultEntity) => {
                        return this.handleFinalAcsWrittenResult(completedResult, buttonConfig);
                    }));

            }), catchError(err => {
                acsWrittenResult.AcsWrittenTestStatusId = AcsWrittenTestStatusAllItems.FailedInvalid;

                this.dwModalService
                    .showDialog('ACS Written Submission Failed', 'Your ACS Written submission failed to process. Please ensure you have uploaded a valid PDF or image file.', buttonConfig)
                    .pipe(take(1))
                    .subscribe(() => { }, () => { });

                return of(acsWrittenResult);
            }));
    }

    private handleFinalAcsWrittenResult(acsWrittenResult: AcsWrittenResultEntity, buttonConfig: DwButtonConfig[]): Observable<AcsWrittenResultEntity> {
        let ob: Observable<any>;

        switch (acsWrittenResult.AcsWrittenTestStatusId) {
            case AcsWrittenTestStatusAllItems.FailedInvalid:
                ob = this.dwModalService.showDialog('ACS Written Submission Failed', 'Your ACS Written submission failed to process. Please ensure you have uploaded a valid PDF or image file.', buttonConfig)
                break;
            case AcsWrittenTestStatusAllItems.Success:
                if (acsWrittenResult.Passed) {
                    ob = this.dwModalService.showDialog('ACS Written Submission Successful', 'Contratulations on passing your ACS Written test!  Click ok to see your test details.', buttonConfig)
                } else {
                    ob = this.dwModalService.showDialog('ACS Written Submission Successful', 'Your ACS written test has been processed, but unfortunately you did not pass.  We have created an assignment based on your missed ACS codes and cleared processed to help in weeker areas.  Also review any AI summaries for study tips.', buttonConfig)
                }
                break;
            case AcsWrittenTestStatusAllItems.New:
                ob = this.dwModalService.showDialog('ACS Written is Queued', 'Your ACS Written test is currently queued and awaiting processing.  Please check back later.', buttonConfig)
                break;
            case AcsWrittenTestStatusAllItems.Processing:
                ob = this.dwModalService.showDialog('ACS Written is Processing', 'Your ACS Written test is currently being processed.  Please check back later.', buttonConfig)
                break;
        }

        if (!ob) {
            ob = of(acsWrittenResult);
        }

        // Keep the status dialog in the workflow, but return the ACS entity rather
        // than the modal result so upload consumers can update their local state.
        return ob.pipe(map(() => {
            this.dwEventService.publishEvent(
                DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.AcsWrittenResult),
                acsWrittenResult
            );

            return acsWrittenResult;
        }));
    }

    /**
     * Waits for an ACS Written Result to finish processing.
     * Uses pub/sub first when available, with polling as a fallback.
     */
    waitForCompletion(acsWrittenResult: AcsWrittenResultEntity): Observable<AcsWrittenResultEntity> {
        if (!acsWrittenResult || !acsWrittenResult.AcsWrittenResultId) {
            return throwError(() => new Error('AcsWrittenResultId is required to wait for completion.'));
        }

        const acsWrittenResultId = acsWrittenResult.AcsWrittenResultId;

        const polling$ = timer(0, this.ACS_INTERVAL_CHECK_TIME_MS).pipe(
            switchMap(() => this.studentRepositoryService.loadAcsWrittenResult(acsWrittenResultId)),
            filter((result: AcsWrittenResultEntity | null | undefined): result is AcsWrittenResultEntity => !!result),
            distinctUntilChanged((previous, current) =>
                previous?.AcsWrittenTestStatusId === current?.AcsWrittenTestStatusId
            ),
            filter((result: AcsWrittenResultEntity) => this.isFinalStatus(result.AcsWrittenTestStatusId))
        );

        const completionEvent$ = this.dwEventService
            .getEventData(DwPubSubEventTopics.messageReceived(ACSSubmissionUtilService.ACS_WRITTEN_RESULT_TOPIC))
            .pipe(
                filter((event: AcsWrittenResultEntity | null | undefined): event is AcsWrittenResultEntity => !!event),
                filter((event: AcsWrittenResultEntity) => event.AcsWrittenResultId === acsWrittenResultId),
                switchMap(() => this.studentRepositoryService.loadAcsWrittenResult(acsWrittenResultId)),
                filter((result: AcsWrittenResultEntity | null | undefined): result is AcsWrittenResultEntity => !!result),
                filter((result: AcsWrittenResultEntity) => this.isFinalStatus(result.AcsWrittenTestStatusId))
            );

        return merge(completionEvent$, polling$).pipe(
            take(1),
            timeout(this.ACS_TIMEOUT_MS),
            takeUntil(this.destroy$),
            catchError((error) => {
                return throwError(() => error);
            })
        );
    }

    private isFinalStatus(statusId: number | null | undefined): boolean {
        return statusId === AcsWrittenTestStatusAllItems.Success
            || statusId === AcsWrittenTestStatusAllItems.FailedInvalid
            || statusId === AcsWrittenTestStatusAllItems.Deleted;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }



}
